#!/usr/bin/env node

/**
 * HyperTask CLI - Production CLI for HyperTask task management system
 * Usage: hypertask [command] [options]
 */

import { fileURLToPath } from "url";
import { dirname, join } from "path";
import { readFileSync } from "fs";
import { program, type Command } from "commander";
import updateNotifier from "update-notifier";
import { execSync } from "child_process";
import latestVersion from "latest-version";
import semver from "semver";
import { CLI_CONFIG } from "./config.js";
import { authLogin, authStatus, authLogout } from "./core/auth.js";
import { autoRefreshSavedTokenIfNeeded, refreshTokenCommand } from "./core/token.js";
import { showContext } from "./core/context.js";
import {
  listProjects,
  listSections,
  listMembers,
  listLabels,
  createLabel,
  createSection,
  createBoardFromManifest,
  addMember,
  updateSection,
  reorderSection,
  deleteSection,
} from "./core/projects.js";
import {
  listTasks,
  TASK_LIST_SEARCH_HELP,
  getTask,
  createTask,
  updateTask,
  assignTask,
  unassignTask,
  moveTask,
  moveTaskToBoard,
  getTaskTree,
  validateTaskUpdateClearOptions,
} from "./core/tasks.js";
import { searchTasks } from "./core/search.js";
import {
  listComments,
  addComment,
  resolveCommentAddText,
  updateComment,
  deleteComment,
} from "./core/comments.js";
import {
  listSkills,
  getSkill,
  createSkill,
  updateSkill,
  deleteSkill,
  importSkills,
} from "./core/skills.js";
import {
  createDraft,
  listDrafts,
  updateDraft,
  publishDraft,
  deleteDraft,
} from "./core/drafts.js";
import { listInbox, archiveInbox, unarchiveInbox } from "./core/inbox.js";
import { dumpCapabilities } from "./core/capabilities.js";
import { formatSuccess, formatInfo, formatError } from "./utils/format.js";
import { cliError, parseList, parseNumber, parsePageLimit, validateTicketNumber } from "./utils/errors.js";
import { getView, listViews } from "./core/view.js";
import { setCliVersion } from "./utils/client.js";
import { updateUser } from "./core/user.js";
import { readCommandDescription } from "./core/read-output-help.js";

// Read version from package.json (avoids drift when publishing)
function getVersion(): string {
  try {
    const __dirname = dirname(fileURLToPath(import.meta.url));
    const pkgPath = join(__dirname, "..", "package.json");
    const pkg = JSON.parse(readFileSync(pkgPath, "utf8"));
    return pkg.version || "0.0.0";
  } catch {
    return "0.0.0";
  }
}
const VERSION = getVersion();

// Server-driven update notice (HTPR-4005): every API call reports x-cli-latest-version,
// and the response interceptor warns on stderr if outdated. Runs even under --json,
// since stderr doesn't touch the JSON on stdout.
setCliVersion(VERSION);

// Check for updates (only shows notification, doesn't block execution)
// Skip update check if --json flag is present to avoid polluting JSON output
if (!process.argv.includes("--json")) {
  try {
    const notifier = updateNotifier({
      pkg: {
        name: "@hypertask/hypertask_cli",
        version: VERSION,
      },
      updateCheckInterval: 1000 * 60 * 5, // Check for updates every 5 minutes
      shouldNotifyInNpmScript: true, // Show notifications even when run as npm script
    });

    // notify() will show notification if update is available
    // Note: update-notifier uses a two-run cycle:
    // - First run: Checks in background (no notification)
    // - Second run: Shows notification if update was found on first run
    notifier.notify();
  } catch (error) {
    // Silently fail if update check fails (e.g., no network, npm registry down)
    // Don't interrupt user's workflow
  }
}

// Create program
const cli = program
  .name("hypertask")
  .description("CLI tool for HyperTask task management")
  .version(VERSION)
  .option("--token <jwt>", "JWT token (overrides saved token)")
  .option("--api-url <url>", `API URL (default: ${CLI_CONFIG.DEFAULT_API_URL})`)
  .option("--json", "Display raw output as JSON");

cli.configureHelp({ showGlobalOptions: true });

function collectAttachPath(value: string, previous: string[]): string[] {
  return [...previous, value];
}

// Global options
interface GlobalOptions {
  token?: string;
  apiUrl?: string;
  json?: boolean;
}

function isTokenRefreshAction(actionCommand: Command): boolean {
  return (
    actionCommand.name() === "refresh" &&
    actionCommand.parent?.name() === "token"
  );
}

function hasTokenOption(argv: string[]): boolean {
  return argv.some((arg) => arg === "--token" || arg.startsWith("--token="));
}

cli.hook("preAction", async (thisCommand, actionCommand) => {
  if (isTokenRefreshAction(actionCommand)) {
    return;
  }

  const globalOpts = thisCommand.opts<GlobalOptions>();
  await autoRefreshSavedTokenIfNeeded({
    cliToken: globalOpts.token,
    hasTokenOverride: hasTokenOption(process.argv),
  });
});

// === AUTH COMMANDS ===
// cli
//   .command("auth:login")
//   .description("Save JWT token")
//   .option("--token <jwt>", "JWT token")
//   .option("--api-url <url>", "API URL")
//   .action(async (options: any) => {
//     const globalOpts = cli.opts<GlobalOptions>();
//     await authLogin(globalOpts.token, globalOpts.apiUrl);
//   });

// cli
//   .command("auth:logout")
//   .description("Clear saved authentication")
//   .action(async () => {
//     const globalOpts = cli.opts<GlobalOptions>();
//     await authLogout(globalOpts.json);
//   });

cli
  .command("login")
  .description("Login to HyperTask via browser")
  .option("--token <jwt>", "JWT token (skip browser auth)")
  .option("--api-url <url>", "API URL")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await authLogin(globalOpts.token, globalOpts.apiUrl);
  });

cli
  .command("logout")
  .description("Logout from HyperTask")
  .action(async () => {
    const globalOpts = cli.opts<GlobalOptions>();
    await authLogout(globalOpts.json, globalOpts.token);
  });

cli
  .command("status")
  .description("Check authentication status")
  .action(async (options: GlobalOptions) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await authStatus(globalOpts.json);
  });

// === TOKENS ===
const tokenCmd = cli.command("token").description("Manage authentication tokens");

tokenCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'token ${unknown}' is not a valid subcommand. Run 'hypertask token --help' to see available subcommands.`,
  );
  process.exit(1);
});

tokenCmd
  .command("refresh")
  .description("Refresh the current JWT token")
  .action(async () => {
    const globalOpts = cli.opts<GlobalOptions>();
    await refreshTokenCommand(
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

cli
  .command("update")
  .description("Update HyperTask CLI to the latest version")
  .action(async () => {
    const globalOpts = cli.opts<GlobalOptions>();

    if (globalOpts.json) {
      // For JSON output, just check and report
      try {
        const notifier = updateNotifier({
          pkg: {
            name: "@hypertask/hypertask_cli",
            version: VERSION,
          },
        });

        const update = notifier.update;
        if (update) {
          console.log(
            JSON.stringify({
              current: update.current,
              latest: update.latest,
              available: true,
            }),
          );
        } else {
          console.log(
            JSON.stringify({
              current: VERSION,
              latest: VERSION,
              available: false,
            }),
          );
        }
      } catch (error) {
        console.log(
          JSON.stringify({
            error: "Failed to check for updates",
            current: VERSION,
          }),
        );
      }
      return;
    }

    // Regular output - check directly for immediate results
    try {
      console.log(formatInfo("Checking for updates..."));

      const latest = await latestVersion("@hypertask/hypertask_cli");
      const cur = semver.coerce(VERSION);
      const reg = semver.coerce(latest);
      if (!cur || !reg) {
        console.error(
          formatError(
            "Could not compare versions; update manually: npm install -g @hypertask/hypertask_cli",
          ),
        );
        process.exit(1);
      }

      if (semver.eq(cur, reg)) {
        console.log(
          formatSuccess(`You're using the latest version (${VERSION})`),
        );
        return;
      }
      if (semver.gt(cur, reg)) {
        console.log(
          formatInfo(
            `You're on ${VERSION}, which is newer than npm's "latest" tag (${latest}). No upgrade from the registry.`,
          ),
        );
        return;
      }

      console.log(formatInfo(`Update available: ${VERSION} → ${latest}`));
      console.log(formatInfo("Installing update..."));

      // --force avoids EEXIST when the global bin already exists (common with custom npm prefix).
      execSync("npm install -g @hypertask/hypertask_cli@latest --force", {
        stdio: "inherit",
      });

      console.log(formatSuccess(`Successfully updated to version ${latest}`));
      console.log(formatInfo("Run `hypertask --version` to verify"));
    } catch (error: any) {
      if (error.status === 1) {
        // npm install failed, but we already showed the error via stdio: 'inherit'
        process.exit(1);
      } else {
        console.error(formatError(`Failed to update: ${error.message}`));
        console.log(
          formatInfo(
            "You can manually update by running: npm install -g @hypertask/hypertask_cli",
          ),
        );
        process.exit(1);
      }
    }
  });

// === CONTEXT ===
cli
  .command("context")
  .description("Get user context, projects, and agent information")
  .action(async () => {
    const globalOpts = cli.opts<GlobalOptions>();
    await showContext(globalOpts.token, globalOpts.apiUrl, globalOpts.json);
  });

// === USER ===
const userCmd = cli
  .command("user")
  .description("Manage user profile");

userCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'user ${unknown}' is not a valid subcommand. Run 'hypertask user --help' to see available subcommands.`,
  );
  process.exit(1);
});

userCmd.addHelpText(
  "after",
  `
Tips for agents:
  Update display name      : hypertask user update --name <displayName>
  Update profile photo     : hypertask user update --photo <url>
`,
);

userCmd
  .command("update")
  .description("Update current user profile")
  .option("--name <displayName>", "Display name")
  .option("--photo <url>", "Profile photo URL")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await updateUser(
      options.name,
      options.photo,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

// === CAPABILITIES ===
// Self-describing catalog of every command + option, so agents driving the CLI
// can enumerate the full surface in one call instead of grepping --help.
cli
  .command('capabilities')
  .alias('commands')
  .description('Dump every command + option as JSON (full capability catalog for agents)')
  .action(async () => {
    const globalOpts = cli.opts<GlobalOptions>();
    dumpCapabilities(cli, globalOpts.json);
  });

// === PROJECTS ===
const projectCmd = cli
  .command("project")
  .alias("projects")
  .description("Manage projects/boards");

projectCmd.on("command:*", ([unknown]: string[]) => {
  const suggestions: Record<string, string> = {
    search: "hypertask search <query>",
    find: "hypertask search <query>",
    query: "hypertask search <query>",
  };
  const hint = suggestions[unknown]
    ? `Did you mean: ${suggestions[unknown]}`
    : `Run 'hypertask project --help' to see available subcommands.`;
  console.error(
    `Error: 'project ${unknown}' is not a valid subcommand. ${hint}`,
  );
  process.exit(2);
});

projectCmd.addHelpText(
  "after",
  `
Tips for agents:
  Search tasks by keyword : hypertask search <query> [--project <id>]
  Find all project IDs    : hypertask project list
  List tasks in a project : hypertask task list --project <id>
`,
);

projectCmd
  .command("list")
  .description(readCommandDescription("List all projects"))
  .option("--limit <n>", "Results per page", "10")
  .option("--offset <n>", "Pagination offset", "0")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await listProjects(globalOpts.token, globalOpts.apiUrl, {
      limit: parsePageLimit(options.limit),
      offset: parseNumber(options.offset, "offset"),
      json: globalOpts.json,
    });
  });

projectCmd
  .command("members <project-id>")
  .description(readCommandDescription("List project members"))
  .action(async (projectId: string) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await listMembers(
      projectId,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

projectCmd
  .command("invite <project-id>")
  .description("Invite user/agent to the board")
  .option("--user <id|email>", "User id (number) or email")
  .action(async (projectId: string, options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await addMember(
      parseInt(projectId),
      options.user,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

projectCmd
  .command("labels <project-id>")
  .description(readCommandDescription("List available labels for a project"))
  .action(async (projectId: string) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await listLabels(
      projectId,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

const labelCmd = projectCmd.command("label").description("Manage labels");

labelCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'project label ${unknown}' is not a valid subcommand. Run 'hypertask project label --help' to see available subcommands.`,
  );
  process.exit(1);
});

labelCmd.addHelpText(
  "after",
  `
Tips for agents:
  List existing labels     : hypertask project labels <project-id>
  Filter tasks by label    : hypertask task list --label <name-or-id> --project <id>
`,
);

labelCmd
  .command("create")
  .description("Create a new label in a project")
  .requiredOption("--project <id>", "Project ID")
  .requiredOption("--name <name>", "Label name")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await createLabel(
      parseInt(options.project),
      options.name,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

projectCmd
  .command("create-board")
  .description(
    "Create a board from JSON (MCP create_board manifest: team_id, title, sections[], optional labels[], tasks[])",
  )
  .option("--manifest <path>", "Path to JSON manifest file")
  .option(
    "--stdin",
    "Read JSON from stdin (pipe a file: cat manifest.json | hypertask project create-board --stdin)",
  )
  .action(async (options: { manifest?: string; stdin?: boolean }) => {
    const globalOpts = cli.opts<GlobalOptions>();
    if (!options.manifest && !options.stdin) {
      console.error(formatError("Provide --manifest <file.json> or --stdin"));
      process.exit(1);
    }
    if (options.manifest && options.stdin) {
      console.error(formatError("Use either --manifest or --stdin, not both"));
      process.exit(1);
    }
    await createBoardFromManifest({
      manifestPath: options.manifest,
      stdin: !!options.stdin,
      token: globalOpts.token,
      apiUrl: globalOpts.apiUrl,
      json: globalOpts.json,
    });
  });

// === SECTIONS ===

const sectionsCmd = cli
  .command("section")
  .alias("sections")
  .description("Manage sections/columns");

sectionsCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'section ${unknown}' is not a valid subcommand. Run 'hypertask section --help' to see available subcommands.`,
  );
  process.exit(1);
});

sectionsCmd.addHelpText(
  "after",
  `
Tips for agents:
  Find a project ID        : hypertask project list
  Move a task to a section : hypertask task move <ticket> --section <name>
`,
);

sectionsCmd
  .command("list")
  .requiredOption("--project <id>", "Project ID")
  .description(readCommandDescription("List sections in a project"))
  .action(async (options: any) => {
    if (!options.project) {
      sectionsCmd.outputHelp();
      return;
    }
    const globalOpts = cli.opts<GlobalOptions>();
    await listSections(
      options.project,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

sectionsCmd
  .command("create")
  .description("Create a new section in a project")
  .requiredOption("--project <id>", "Project ID")
  .requiredOption(
    "--title <name>",
    'Section title (e.g. "Backlog", "In Review")',
  )
  .option("--after <id>", "Place new section after this section ID")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await createSection(
      parseInt(options.project),
      options.title,
      options.after ? parseInt(options.after) : undefined,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

//Rename a section
sectionsCmd
  .command("rename")
  .description("Rename a section in the project")
  .requiredOption("--project <id>", "Project ID")
  .requiredOption("--section <id>", "Section ID")
  .requiredOption(
    "--title <name>",
    'Section title (e.g. "Backlog", "In Review")',
  )
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await updateSection(
      parseInt(options.project),
      parseInt(options.section),
      options.title,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

//Reorder a section
sectionsCmd
  .command("reorder")
  .description("Move a section after another section in the project")
  .requiredOption("--project <id>", "Project ID")
  .requiredOption("--section <id|name>", "Section ID or name")
  .requiredOption("--after <id|name>", "Section ID or name to place this section after")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await reorderSection(
      parseInt(options.project),
      options.section,
      options.after,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

//Delete a section
sectionsCmd
  .command("delete")
  .description("Delete a section in the project")
  .requiredOption("--project <id>", "Project ID")
  .requiredOption("--section <id>", "Section ID")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await deleteSection(
      parseInt(options.project),
      parseInt(options.section),
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

// === TASKS ===
const taskCmd = cli.command("task").alias("tasks").description("Manage tasks");

taskCmd.on("command:*", ([unknown]: string[]) => {
  const suggestions: Record<string, string> = {
    search: "hypertask search <query>",
    find: "hypertask search <query>",
    query: "hypertask search <query>",
  };
  const hint = suggestions[unknown]
    ? `Did you mean: ${suggestions[unknown]}`
    : `Run 'hypertask task --help' to see available subcommands.`;
  console.error(`Error: 'task ${unknown}' is not a valid subcommand. ${hint}`);
  process.exit(1);
});

taskCmd.addHelpText(
  "after",
  `
Tips for agents:
  Search tasks by keyword  : hypertask search <query> [--project <id>]
  Find a user ID           : hypertask project members <project-id>
  Find a section ID        : hypertask section list --project <project-id>
  Find a label ID          : hypertask project labels <project-id>
  Find a project ID        : hypertask project list
  Attach a file to a task  : hypertask task create --attach <path|url>  (or: task update <ticket> --attach <path|url>)
  Full command catalog     : hypertask capabilities --json
`,
);

taskCmd
  .command("list")
  .description(readCommandDescription("List tasks with filters"))
  .option("--project <id>", "Project ID")
  .option("--search <query>", TASK_LIST_SEARCH_HELP)
  .option(
    "--section <id|name>",
    "Section column ID or name (name requires --project)",
  )
  .option("--assigned-to <value>", "Assignee (me, unassigned, or user ID)")
  .option("--created-by <id>", "Creator user ID (numeric; server-side filter)")
  .option(
    "--priority <list>",
    "Comma-separated priorities: urgent,high,medium,low",
  )
  .option(
    "--label <list>",
    "Comma-separated label names or IDs (names need --project)",
  )
  .option(
    "--sort-by <field>",
    "Sort tasks by: createdAt, updatedAt, dueDate, priority, title",
  )
  .option(
    "--sort-order <asc|desc>",
    "Set the sort order of results by asc or desc",
  )
  .option("--limit <n>", "Results per page", "10")
  .option("--offset <n>", "Pagination offset", "0")
  .option(
    "--status <field>",
    "Filter tasks by status (Normal, Archive, Deleted)",
  )
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await listTasks(globalOpts.token, globalOpts.apiUrl, {
      search: options.search,
      project: options.project ? parseInt(options.project) : undefined,
      section: options.section,
      assignedTo: options.assignedTo,
      createdBy:
        options.createdBy != null ? parseInt(options.createdBy, 10) : undefined,
      priority: options.priority,
      labels: options.label,
      sortBy: options.sortBy,
      sortOrder: options.sortOrder,
      limit: parseInt(options.limit),
      offset: parseInt(options.offset),
      status: options.status,
      json: globalOpts.json,
    });
  });

taskCmd
  .command("get <ticket>")
  .alias("show")
  .description(readCommandDescription("Get task details"))
  .action(async (ticket: string) => {
    validateTicketNumber(ticket);
    const globalOpts = cli.opts<GlobalOptions>();
    await getTask(ticket, globalOpts.token, globalOpts.apiUrl, globalOpts.json);
  });

taskCmd
  .command("tree")
  .description("Show full parent/subtask hierarchy for a task")
  .option("--ticket <ticket>", "Ticket number (e.g. HTPR-15)")
  .option("--task-id <id>", "Numeric task ID")
  .option("--depth <n>", "Max levels deep to show (default: full tree)")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await getTaskTree(globalOpts.token, globalOpts.apiUrl, {
      ticket: options.ticket,
      taskId:
        options.taskId != null
          ? parseInt(String(options.taskId), 10)
          : undefined,
      depth:
        options.depth != null ? parseInt(String(options.depth), 10) : undefined,
      json: globalOpts.json,
    });
  });

taskCmd
  .command("create")
  .description("Create a new task")
  .requiredOption("--project <id>", "Project ID")
  .requiredOption("--title <text>", "Task title")
  .option("--description <text>", "Task description")
  .option("--priority <p>", "Priority: urgent, high, medium, low, none")
  .option("--estimate <n>", "Story points")
  .option(
    "--labels <list>",
    'Comma-separated label names or IDs (e.g. "CLI,BUG")',
  )
  .option("--due <date>", "Due date (ISO 8601)")
  .option("--parent-task <id>", "Parent task ID")
  .option("--section <id|name>", 'Section ID or name (e.g. 4005 or "Doing")')
  .option(
    "--assignee <list>",
    'Comma-separated user IDs (e.g. "123" or "123,456,789")',
  )
  .option(
    "--attach <path-or-url>",
    "Attach local file or http(s) URL after task is created (repeatable)",
    collectAttachPath,
    [],
  )
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await createTask(globalOpts.token, globalOpts.apiUrl, {
      project: parseInt(options.project),
      title: options.title,
      description: options.description,
      priority: options.priority,
      estimate: options.estimate ? parseInt(options.estimate) : undefined,
      labels: options.labels,
      due: options.due,
      attach: options.attach?.length ? options.attach : undefined,
      parentTaskId: options.parentTask
        ? parseInt(options.parentTask)
        : undefined,
      section: options.section,
      assignee: options.assignee
        ? options.assignee.split(",").map((id: string) => parseInt(id))
        : undefined,
      json: globalOpts.json,
    });
  });

taskCmd
  .command("update <ticket>")
  .description("Update a task")
  .option("--title <text>", "New title")
  .option("--description <text>", "New description (plain text or HTML)")
  .option("--priority <p>", "New priority")
  .option("--estimate <n>", "New estimate")
  .option("--due <date>", "New due date")
  .option("--clear-due", "Remove the task's due date")
  .option("--status <s>", "New status: Normal, Archive, Deleted")
  .option("--section <id|name>", 'Section ID or name (e.g. 4005 or "Doing")')
  .option(
    "--project <id>",
    "Project ID (required when using --section with section name across projects)",
  )
  .option(
    "--assignee <list>",
    'Comma-separated user IDs (e.g. "123" or "123,456,789")',
  )
  .option(
    "--labels <list>",
    'Comma-separated label names or IDs (e.g. "CLI,BUG")',
  )
  .option("--parent-task <id>", "Parent task ID")
  .option("--clear-parent", "Detach this task from its parent")
  .option(
    "--attach <path>",
    "Attach local file to task after update (repeatable)",
    collectAttachPath,
    [],
  )
  .action(async (ticket: string, options: any) => {
    validateTicketNumber(ticket);
    validateTaskUpdateClearOptions(options);
    const globalOpts = cli.opts<GlobalOptions>();
    await updateTask(ticket, globalOpts.token, globalOpts.apiUrl, {
      title: options.title,
      description: options.description,
      priority: options.priority,
      estimate: options.estimate ? parseInt(options.estimate) : undefined,
      due: options.due,
      clearDue: options.clearDue,
      status: options.status,
      section: options.section,
      project: options.project ? parseInt(options.project) : undefined,
      labels: options.labels,
      attach: options.attach?.length ? options.attach : undefined,
      assignee: options.assignee
        ? options.assignee.split(",").map((id: string) => parseInt(id, 10))
        : undefined,
      parentTaskId: options.parentTask
        ? parseInt(options.parentTask)
        : undefined,
      clearParent: options.clearParent,
      json: globalOpts.json,
    });
  });

taskCmd
  .command("assign <ticket>")
  .description("Assign user to task (idempotent: repeat assign is a no-op)")
  .requiredOption("--assignee <id>", "User ID")
  .action(async (ticket: string, options: any) => {
    validateTicketNumber(ticket);
    const globalOpts = cli.opts<GlobalOptions>();
    await assignTask(
      ticket,
      parseInt(options.assignee, 10),
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

taskCmd
  .command("unassign <ticket>")
  .description("Remove a user from task assignees")
  .requiredOption("--user <id>", "User ID to remove from assignees")
  .action(async (ticket: string, options: any) => {
    validateTicketNumber(ticket);
    const globalOpts = cli.opts<GlobalOptions>();
    await unassignTask(
      ticket,
      parseInt(options.user, 10),
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

taskCmd
  .command("move <ticket-or-task-id>")
  .description(
    "Move task: use ticket + --section for same board, or numeric task id + --to for another project",
  )
  .option(
    "--section <name>",
    'Destination section name within same board (e.g. "Doing", "Done")',
  )
  .option(
    "--to <id>",
    "Target project ID — cross-board move (first arg must be numeric task id)",
  )
  .option(
    "--to-section <id>",
    "Target section ID in destination project (use with --to)",
  )
  .action(async (ticketOrTaskId: string, options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    if (options.to != null) {
      const taskId = parseInt(ticketOrTaskId, 10);
      if (!Number.isInteger(taskId) || taskId < 1) {
        console.error(
          "Error: With --to, first argument must be numeric task id (not ticket number)",
        );
        process.exit(1);
      }
      await moveTaskToBoard(
        taskId,
        parseInt(options.to),
        options.toSection ? parseInt(options.toSection) : undefined,
        globalOpts.token,
        globalOpts.apiUrl,
        globalOpts.json,
      );
    } else if (options.section) {
      validateTicketNumber(ticketOrTaskId);
      await moveTask(
        ticketOrTaskId,
        options.section,
        globalOpts.token,
        globalOpts.apiUrl,
        globalOpts.json,
      );
    } else {
      console.error(
        "Error: Specify either --section <name> (same board, first arg = ticket) or --to <project-id> (cross-board, first arg = task id)",
      );
      process.exit(1);
    }
  });

taskCmd
  .command("move-board <task-id>")
  .description(
    "Move task to a different project/board (first arg = numeric task id)",
  )
  .alias("move-project")
  .requiredOption("--target-project <id>", "Target project ID")
  .option("--target-section <id>", "Target section ID")
  .action(async (taskIdStr: string, options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    const taskId = parseInt(taskIdStr, 10);
    if (!Number.isInteger(taskId) || taskId < 1) {
      console.error("Error: task-id must be a positive integer");
      process.exit(1);
    }
    await moveTaskToBoard(
      taskId,
      parseInt(options.targetProject),
      options.targetSection ? parseInt(options.targetSection) : undefined,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

// === DRAFTS ===
const draftCmd = cli
  .command("draft")
  .alias("drafts")
  .description("Create and manage comment/description drafts for tasks");

draftCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'draft ${unknown}' is not a valid subcommand. Run 'hypertask draft --help' to see available subcommands.`,
  );
  process.exit(1);
});

draftCmd.addHelpText(
  "after",
  `
Tips for agents:
  Drafts are per-ticket. Get a ticket number from : hypertask task list
  To publish a draft as a comment or description  : hypertask draft publish <draft-id>
`,
);

draftCmd
  .command("create <ticket>")
  .description(
    "Create a draft for a task. Add --comment to create draft for comment, else draft created for description.",
  )
  .option(
    "--text <text>",
    "Draft text (plain text or markdown; converted to HTML automatically)",
  )
  .option(
    "--comment",
    "If provided, draft is created for comment; otherwise description draft",
  )
  .action(async (ticket: string, options: any) => {
    validateTicketNumber(ticket);
    const globalOpts = cli.opts<GlobalOptions>();
    await createDraft(
      ticket,
      {
        text: options.text,
        comment: options.comment,
        json: globalOpts.json,
      },
      globalOpts.token,
      globalOpts.apiUrl,
    );
  });

draftCmd
  .command("list <ticket>")
  .description("List current draft(s) for a ticket")
  .action(async (ticket: string) => {
    validateTicketNumber(ticket);
    const globalOpts = cli.opts<GlobalOptions>();
    await listDrafts(
      ticket,
      { json: globalOpts.json },
      globalOpts.token,
      globalOpts.apiUrl,
    );
  });

draftCmd
  .command("update <draft-id>")
  .description("Update an existing draft")
  .requiredOption(
    "--text <text>",
    "New draft text (plain text or markdown; converted to HTML automatically)",
  )
  .action(async (draftId: string, options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await updateDraft(
      parseInt(draftId, 10),
      {
        text: options.text,
        json: globalOpts.json,
      },
      globalOpts.token,
      globalOpts.apiUrl,
    );
  });

draftCmd
  .command("publish <draft-id>")
  .description(
    "Publish a draft — creates the actual comment or updates the task description from the draft",
  )
  .action(async (draftId: string) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await publishDraft(
      parseInt(draftId, 10),
      { json: globalOpts.json },
      globalOpts.token,
      globalOpts.apiUrl,
    );
  });

draftCmd
  .command("delete <draft-id>")
  .description("Delete a draft")
  .action(async (draftId: string) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await deleteDraft(
      parseInt(draftId, 10),
      { json: globalOpts.json },
      globalOpts.token,
      globalOpts.apiUrl,
    );
  });

// === SEARCH ===
cli
  .command("search <query>")
  .description(readCommandDescription("Search tasks"))
  .option("--project <id>", "Project ID")
  .option("--limit <n>", "Max results", "10")
  .action(async (query: string, options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await searchTasks(query, globalOpts.token, globalOpts.apiUrl, {
      project: options.project ? parseInt(options.project) : undefined,
      limit: parseInt(options.limit),
      json: globalOpts.json,
    });
  });

// === COMMENTS ===
const commentCmd = cli
  .command("comment")
  .alias("comments")
  .description("Manage comments");

commentCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'comment ${unknown}' is not a valid subcommand. Run 'hypertask comment --help' to see available subcommands.`,
  );
  process.exit(1);
});

commentCmd.addHelpText(
  "after",
  `
Tips for agents:
  Comments are attached to tasks identified by ticket number (e.g. HTPR-42)
  To get a ticket number    : hypertask task list [--project <id>]
  Attach a file to a comment: hypertask comment add <ticket> --attach <path|url>  (repeatable; local path or http(s) URL)
`,
);

commentCmd
  .command("list <ticket>")
  .description(readCommandDescription("List comments on a task"))
  .action(async (ticket: string) => {
    validateTicketNumber(ticket);
    const globalOpts = cli.opts<GlobalOptions>();
    await listComments(
      ticket,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

commentCmd
  .command("add <ticket>")
  .description("Add comment to task")
  .option("--text <text>", "Comment text")
  .option("--body <body>", "Alias for --text")
  .option(
    "--attach <path-or-url>",
    "Attach local file or http(s) URL to comment (repeatable)",
    collectAttachPath,
    [],
  )
  .action(async (ticket: string, options: any) => {
    validateTicketNumber(ticket);
    const globalOpts = cli.opts<GlobalOptions>();
    let text: string;
    try {
      text = resolveCommentAddText(options);
    } catch (error) {
      cliError(error instanceof Error ? error.message : String(error));
    }
    await addComment(
      ticket,
      text,
      globalOpts.token,
      globalOpts.apiUrl,
      {
        attach: options.attach?.length ? options.attach : undefined,
        json: globalOpts.json,
      },
    );
  });

commentCmd
  .command("update <id>")
  .description("Update comment")
  .requiredOption("--text <text>", "New comment text")
  .action(async (id: string, options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await updateComment(
      parseInt(id),
      options.text,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

commentCmd
  .command("delete <id>")
  .description("Delete comment")
  .action(async (id: string) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await deleteComment(
      parseInt(id),
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

// === SKILLS ===
const skillsCmd = cli
  .command("skills")
  .alias("skill")
  .description("Manage personal and project skills");

skillsCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'skills ${unknown}' is not a valid subcommand. Run 'hypertask skills --help' to see available subcommands.`,
  );
  process.exit(1);
});

skillsCmd.addHelpText(
  "after",
  `
Tips for agents:
  Personal skills omit project : hypertask skills list
  Project skills need project ID: hypertask skills list --project <id>
  Create from a SKILL.md file   : hypertask skills create --markdown <path>
  Import skills from GitHub     : hypertask skills import <github-url> [--dry-run]
  Enabled skills run as /slug in AI chat or from @hyperai comments
`,
);

skillsCmd
  .command("list")
  .description(readCommandDescription("List personal or project skills"))
  .option("--project <id>", "Project ID (omit for personal skills)")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await listSkills(
      options.project ? parseInt(options.project, 10) : undefined,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

skillsCmd
  .command("get <id>")
  .description(readCommandDescription("Get a skill"))
  .action(async (id: string) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await getSkill(
      parseInt(id, 10),
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

skillsCmd
  .command("create")
  .description("Create a personal or project skill")
  .option("--name <name>", "Skill name")
  .option("--slug <slug>", "Skill invocation slug")
  .option("--body <body>", "Skill instruction body")
  .option("--description <description>", "Skill description")
  .option("--argument-hint <hint>", "Skill argument hint")
  .option("--scope <user|project>", "Skill scope", "user")
  .option("--project <id>", "Project ID (required for project scope)")
  .option("--markdown <path>", "Read complete skill content from a SKILL.md file")
  .option("--disabled", "Create the skill disabled")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await createSkill(
      {
        name: options.name,
        slug: options.slug,
        body: options.body,
        description: options.description,
        argumentHint: options.argumentHint,
        scope: options.scope,
        projectId: options.project ? parseInt(options.project, 10) : undefined,
        markdownPath: options.markdown,
        enabled: options.disabled ? false : undefined,
      },
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

skillsCmd
  .command("update <id>")
  .description("Update a skill")
  .option("--name <name>", "New skill name")
  .option("--slug <slug>", "New skill invocation slug")
  .option("--body <body>", "New skill instruction body")
  .option("--description <description>", "New skill description")
  .option("--argument-hint <hint>", "New skill argument hint")
  .option("--enabled", "Enable the skill")
  .option("--disabled", "Disable the skill")
  .option("--markdown <path>", "Read complete updated skill content from a SKILL.md file")
  .action(async (id: string, options: any) => {
    if (options.enabled && options.disabled) {
      cliError("Cannot pass both --enabled and --disabled");
    }
    const globalOpts = cli.opts<GlobalOptions>();
    await updateSkill(
      parseInt(id, 10),
      {
        name: options.name,
        slug: options.slug,
        body: options.body,
        description: options.description,
        argumentHint: options.argumentHint,
        enabled: options.disabled ? false : options.enabled ? true : undefined,
        markdownPath: options.markdown,
      },
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

skillsCmd
  .command("delete <id>")
  .description("Delete a skill")
  .action(async (id: string) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await deleteSkill(
      parseInt(id, 10),
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

skillsCmd
  .command("import <github-url>")
  .description("Import skills from GitHub")
  .option("--scope <user|project>", "Skill scope", "user")
  .option("--project <id>", "Project ID (required for project scope)")
  .option("--dry-run", "Preview skills without importing them")
  .option("--slugs <comma-separated>", "Only import these comma-separated skill slugs")
  .action(async (githubUrl: string, options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await importSkills(
      githubUrl,
      {
        scope: options.scope,
        projectId: options.project ? parseInt(options.project, 10) : undefined,
        dryRun: options.dryRun,
        slugs: options.slugs ? parseList(options.slugs) : undefined,
      },
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

// === INBOX ===
const inboxCmd = cli.command("inbox").description("Manage notifications");

inboxCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'inbox ${unknown}' is not a valid subcommand. Run 'hypertask inbox --help' to see available subcommands.`,
  );
  process.exit(1);
});

inboxCmd.addHelpText(
  "after",
  `
Tips for agents:
  Notification IDs are shown in : hypertask inbox list
  Archive multiple at once      : hypertask inbox archive <id1> <id2> ...
`,
);

inboxCmd
  .command("list")
  .description(readCommandDescription("List notifications"))
  .action(async () => {
    const globalOpts = cli.opts<GlobalOptions>();
    await listInbox(globalOpts.token, globalOpts.apiUrl, globalOpts.json);
  });

inboxCmd
  .command("archive <ids...>")
  .description("Archive notifications")
  .action(async (ids: string[]) => {
    const globalOpts = cli.opts<GlobalOptions>();
    const numIds = ids.map((id) => parseInt(id));
    await archiveInbox(
      numIds,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

inboxCmd
  .command("unarchive <ids...>")
  .description("Restore archived notifications to the inbox")
  .action(async (ids: string[]) => {
    const globalOpts = cli.opts<GlobalOptions>();
    const numIds = ids.map((id) => parseInt(id));
    await unarchiveInbox(
      numIds,
      globalOpts.token,
      globalOpts.apiUrl,
      globalOpts.json,
    );
  });

// === VIEWS ===
const viewCmd = cli.command("view").description("Manage views for projects").alias('views');

viewCmd.on("command:*", ([unknown]: string[]) => {
  console.error(
    `Error: 'view ${unknown}' is not a valid subcommand. Run 'hypertask view --help' to see available subcommands.`,
  );
  process.exit(1);
});

// viewCmd.addHelpText(
//   "after",
//   `
// Tips for agents:
//   Notification IDs are shown in : hypertask inbox list
//   Archive multiple at once      : hypertask inbox archive <id1> <id2> ...
// `,
// );

viewCmd
  .command("list")
  .description(
    readCommandDescription(
      "List all active views in your projects. If --project is provided, all personal and public views are displayed",
    ),
  )
  .option("--project <id>", "Project ID")
  .option("--visibility <value>", "Filter views by 'Public' or 'Private'")
  .option("--limit <n>", "Results per page", "10")
  .option("--offset <n>", "Pagination offset", "0")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    await listViews(globalOpts.token, globalOpts.apiUrl, {
      limit: parsePageLimit(options.limit),
      offset: parseNumber(options.offset, "offset"),
      project_id: options.project,
      visibility: options.visibility,
      json: globalOpts.json,
    });
  });

  viewCmd
  .command("get <id>")
  .alias('show')
  .description("Fetch view details for provided id")
  .action(async (options: any) => {
    const globalOpts = cli.opts<GlobalOptions>();
    const viewId = options.id || options; // fallback for legacy or wrong Commander usage
    await getView(globalOpts.token, globalOpts.apiUrl, {
      viewId,
      json: globalOpts.json,
    });
  });

// Top-level discovery hint (shown at bottom of `hypertask --help`)
cli.addHelpText(
  "after",
  `
Command Groups:
  Core tasks     task list/get/create/update/move/assign/tree
  Projects       project list/members/invite/labels/label/create-board
  Sections       section list/create/rename/reorder/delete
  User           user update
  Discovery      search <query>, context
  Communication  comment list/add/update/delete
  Skills         skills list/get/create/update/delete/import
  Inbox          inbox list/archive/unarchive
  Drafts         draft create/list/update/publish/delete
  Auth           login, logout, status
  Tokens         token refresh

Finding IDs (required by most commands):
  Project IDs    hypertask project list
  User IDs       hypertask project members <project-id>
  Section IDs    hypertask section list --project <project-id>
  Label IDs      hypertask project labels <project-id>

Full machine-readable catalog (every command + option as JSON):
  hypertask capabilities --json
`,
);

// Parse and run
cli.parse(process.argv);

// Show help if no command
if (!process.argv.slice(2).length) {
  cli.outputHelp();
}
